/*
 * Ticket eligibility gate (gender + age) — the R3 launch-critical safety core.
 * Spec: docs/current/ELIGIBILITY-VALIDATION-GENDER-AGE-SPEC-2026-08-11.md
 * (boardroom), dispatch BOARDROOM-2026-08-11-195636.
 *
 * A participant of the wrong gender/age must be BLOCKED from completing
 * get-ready for a ticket they are not eligible for, and routed to the Help Desk
 * (R4). This module is the PURE decision core — it takes the ticket's R1
 * eligibility contract + the participant's own gender/DOB as INPUT and returns a
 * verdict. It has no network/contract dependency of its own: it bites the moment
 * box-office exposes the R1 fields on /consumer/me/orders, and is a safe no-op
 * (allow) until then, because an absent/unknown contract never blocks.
 *
 * Conservative posture (mirrors assignModel.genderEligible, CMT-010806): block
 * ONLY a clear mismatch. Unknown / empty / non-binary / self-described
 * participant gender, an unrestricted (BOTH/null) ticket, an absent age band, or
 * a missing/invalid DOB all resolve to ALLOW — false-blocking a valid
 * participant is worse than the pre-existing gap. The authoritative block is
 * server-side (R2, tickets-laptop); this is the get-ready bite (R3).
 */

/** The ticket's R1 eligibility contract, denormalized onto the box-office
 *  /consumer/me/orders ticket payload. Authored in manage.trumin:
 *  ticket_types.gender ∈ {M,F,BOTH}; age band from division.config
 *  {ageMin, ageMax}. Any field null/absent ⇒ unrestricted on that axis. */
export interface TicketEligibility {
  gender: 'M' | 'F' | 'BOTH' | null
  ageMin: number | null
  ageMax: number | null
}

/** The participant's own attributes at profile submission (in-hand — no
 *  persistence dependency): Cognito `gender` string + `birthdate` (YYYY-MM-DD). */
export interface ParticipantAttrs {
  gender: string | null | undefined
  birthdate: string | null | undefined
}

export type EligibilityVerdict =
  | { ok: true }
  | { ok: false; kind: 'gender' | 'age'; message: string }

const FEMALE_TOKENS = new Set(['female', 'women', 'woman', 'f'])
const MALE_TOKENS = new Set(['male', 'men', 'man', 'm'])

/** Participant gender → binary token, or null when ambiguous (self-describe /
 *  non-binary / empty / unknown) — null NEVER blocks. Exact-token match only. */
function participantBinary(gender: string | null | undefined): 'male' | 'female' | null {
  const g = (gender ?? '').trim().toLowerCase()
  if (!g) return null
  if (FEMALE_TOKENS.has(g)) return 'female'
  if (MALE_TOKENS.has(g)) return 'male'
  return null
}

/** Age in whole years on `onDate` for a `YYYY-MM-DD` birthdate; null if the
 *  birthdate is absent or not a valid ISO date (never block on unknown age).
 *
 *  TIMEZONE (adversarial-review P1-2): evaluated with **UTC date parts
 *  end-to-end** — the raceDay anchor arrives as a UTC instant (events.starts_at
 *  off the wire) and the SERVER evaluates the same rule in UTC, so reading the
 *  anchor with LOCAL getFullYear/getMonth/getDate made a UTC-negative browser
 *  diverge from the server's verdict on a birthday boundary (e.g. a
 *  T00:30:00Z race start reads as the PREVIOUS local day west of Greenwich).
 *  The plain `YYYY-MM-DD` dob is parsed as UTC too (Date.UTC) so both sides of
 *  the comparison live on the same axis. */
export function ageOnDate(birthdate: string | null | undefined, onDate: Date): number | null {
  if (!birthdate || !/^\d{4}-\d{2}-\d{2}$/.test(birthdate)) return null
  const [y, m, d] = birthdate.split('-').map(Number)
  // Reconstruct + validate in UTC (rejects 2026-02-31 etc.).
  const born = new Date(Date.UTC(y, m - 1, d))
  if (born.getUTCFullYear() !== y || born.getUTCMonth() !== m - 1 || born.getUTCDate() !== d) return null
  if (Number.isNaN(onDate.getTime())) return null
  let age = onDate.getUTCFullYear() - y
  const beforeBirthday =
    onDate.getUTCMonth() < m - 1 || (onDate.getUTCMonth() === m - 1 && onDate.getUTCDate() < d)
  if (beforeBirthday) age -= 1
  return age
}

/** Human gender clause for the block message, or '' when unrestricted. */
function genderClause(gender: TicketEligibility['gender']): string {
  if (gender === 'F') return 'female participants'
  if (gender === 'M') return 'male participants'
  return ''
}

/** Age clause for the block message, or '' when no band is set. */
function ageClause(ageMin: number | null, ageMax: number | null): string {
  if (ageMin != null && ageMax != null) return `ages ${ageMin}–${ageMax}`
  if (ageMin != null) return `ages ${ageMin}+`
  if (ageMax != null) return `ages up to ${ageMax}`
  return ''
}

/** The R3 Help-Desk block message: "This ticket is for {gender}/{ages}. Please
 *  contact the Help Desk." Only the restrictive clauses are included. */
export function eligibilityMessage(ticket: TicketEligibility): string {
  const parts = [genderClause(ticket.gender), ageClause(ticket.ageMin, ticket.ageMax)].filter(Boolean)
  const forClause = parts.length ? ` for ${parts.join(', ')}` : ''
  return `This ticket is${forClause}. Please contact the Help Desk.`
}

/**
 * PROFILE-TIME reject copy mapping (BOARDROOM-2026-08-28-194055): Duncan's
 * plain-English enforcement copy, expressed as an i18n key + params so the
 * screens resolve it through the live locale (EN keys in src/i18n/en.ts;
 * Arabic falls back via the ar spread — no machine translation). Mirrors the
 * server's enforcementMessage() axis-for-axis, so the pre-save wizard gate and
 * the surfaced server 422 read identically. Returns null when the contract is
 * not in hand / the failed axis has no expressible restriction — the caller
 * then falls back to the server's own `message` (never fabricates one).
 */
export interface EligibilityRejectCopy {
  key:
    | 'eligibility.reject.gender.male'
    | 'eligibility.reject.gender.female'
    | 'eligibility.reject.age.band'
    | 'eligibility.reject.age.min'
    | 'eligibility.reject.age.max'
    | 'eligibility.mismatch.gender.male'
    | 'eligibility.mismatch.gender.female'
  params?: Record<string, string | number>
}

export function eligibilityRejectCopy(
  reason: 'gender' | 'age',
  ticket: TicketEligibility | null | undefined,
): EligibilityRejectCopy | null {
  if (!ticket) return null
  if (reason === 'gender') {
    if (ticket.gender === 'M') return { key: 'eligibility.reject.gender.male' }
    if (ticket.gender === 'F') return { key: 'eligibility.reject.gender.female' }
    return null
  }
  if (ticket.ageMin != null && ticket.ageMax != null) {
    return { key: 'eligibility.reject.age.band', params: { min: ticket.ageMin, max: ticket.ageMax } }
  }
  if (ticket.ageMin != null) return { key: 'eligibility.reject.age.min', params: { min: ticket.ageMin } }
  if (ticket.ageMax != null) return { key: 'eligibility.reject.age.max', params: { max: ticket.ageMax } }
  return null
}

/**
 * PARTICIPANT-VOICE mismatch copy (BOARDROOM-2026-08-29-120259 ruling 1 +
 * ADDENDUM 2 BOARDROOM-2026-08-29-123126): the fail-fast inline banner and the
 * truthful get-ready mismatch overlay speak to the PARTICIPANT about THEIR
 * ticket ("This ticket is for a male participant"), event-genderized from the
 * ticket's own R1 contract — distinct from the assignment-voice reject copy
 * above ("This is a male ticket and must be assigned…"), which stays on the
 * assign/save surfaces. The age axis reuses the reason-aware reject keys
 * (band / min / max — they already carry the ticket's real numbers, never a
 * fabricated band). Returns null when the in-hand contract cannot express the
 * failed axis — the caller falls back to the server's own message / generic.
 */
export function eligibilityMismatchCopy(
  kind: 'gender' | 'age',
  ticket: TicketEligibility | null | undefined,
): EligibilityRejectCopy | null {
  if (!ticket) return null
  if (kind === 'gender') {
    if (ticket.gender === 'M') return { key: 'eligibility.mismatch.gender.male' }
    if (ticket.gender === 'F') return { key: 'eligibility.mismatch.gender.female' }
    return null
  }
  return eligibilityRejectCopy('age', ticket)
}

/**
 * DOB-YEAR dropdown narrowing (BOARDROOM-2026-09-05-101330): filter a caller-
 * supplied year list down to the years where the child COULD be inside the
 * ticket's [ageMin, ageMax] band on race day. CONSTRAIN-THEN-VALIDATE — this
 * only shapes the OFFERED options; eligibilityGate stays the final arbiter
 * (a boundary year's exact eligibility depends on the full DOB, so boundary
 * years are INCLUSIVE here).
 *
 * The extreme race-day ages for a birth year Y are evaluated through the SAME
 * ageOnDate helper the gate itself uses (one arithmetic, one UTC posture —
 * no parallel year math to drift):
 *   oldest   = ageOnDate(`${Y}-01-01`, raceDay)  (a Jan-1-born child)
 *   youngest = ageOnDate(`${Y}-12-31`, raceDay)  (a Dec-31-born child)
 * Y survives iff that [youngest, oldest] range intersects the band:
 *   ageMin set ⇒ oldest ≥ ageMin   (the OLDEST possible age reaches min)
 *   ageMax set ⇒ youngest ≤ ageMax (the YOUNGEST possible age fits max)
 *
 * HONEST FALLBACK (PROCESS-3 — never fabricate a range): absent/invalid
 * contract or anchor, or a bandless contract, returns the input unchanged; a
 * partial band (min-only / max-only) constrains ONLY its bounded side.
 */
export function constrainYearsToEligibility(
  years: readonly string[],
  ticket: TicketEligibility | null | undefined,
  raceDay: Date | null | undefined,
): string[] {
  if (!ticket || !raceDay || Number.isNaN(raceDay.getTime())) return [...years]
  const { ageMin, ageMax } = ticket
  if (ageMin == null && ageMax == null) return [...years]
  return years.filter((y) => {
    if (!Number.isFinite(Number(y))) return true // never silently drop a non-year entry
    const oldest = ageOnDate(`${y}-01-01`, raceDay)
    const youngest = ageOnDate(`${y}-12-31`, raceDay)
    if (oldest == null || youngest == null) return true // unparsable ⇒ never drop
    return (ageMin == null || oldest >= ageMin) && (ageMax == null || youngest <= ageMax)
  })
}

/**
 * The R3 gate. Given the participant's in-hand gender/DOB and the ticket's R1
 * contract, decide whether get-ready profile completion may proceed.
 *
 * @param raceDay reference date for the age band ("age as of race day"). When
 *   omitted/unknown, the age axis is SKIPPED (allow) — we never block on an age
 *   we cannot anchor to a date. The instant is read with UTC date parts
 *   (ageOnDate) so the FE verdict matches the server's UTC evaluation in every
 *   browser timezone (P1-2).
 */
export function eligibilityGate(
  participant: ParticipantAttrs,
  ticket: TicketEligibility | null | undefined,
  raceDay?: Date | null,
): EligibilityVerdict {
  if (!ticket) return { ok: true } // no R1 contract yet ⇒ safe no-op

  // Gender axis — block only a clear binary-opposite mismatch.
  const restriction = ticket.gender === 'F' ? 'female' : ticket.gender === 'M' ? 'male' : null
  if (restriction) {
    const pb = participantBinary(participant.gender)
    if (pb && pb !== restriction) {
      return { ok: false, kind: 'gender', message: eligibilityMessage(ticket) }
    }
  }

  // Age axis — block only when DOB is known AND clearly outside the band, and we
  // have a race-day anchor. min-only / max-only bands are honoured.
  if (raceDay && (ticket.ageMin != null || ticket.ageMax != null)) {
    const age = ageOnDate(participant.birthdate, raceDay)
    if (age != null) {
      const belowMin = ticket.ageMin != null && age < ticket.ageMin
      const aboveMax = ticket.ageMax != null && age > ticket.ageMax
      if (belowMin || aboveMax) {
        return { ok: false, kind: 'age', message: eligibilityMessage(ticket) }
      }
    }
  }

  return { ok: true }
}
